import React from 'react';
import {
  Editor, Element, Transforms,
} from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import { KatIcon } from '@amzn/katal-react';
import { BasicElement, ElementTypes } from 'src/types/CustomSlateTypes';
import ToolbarButton from './ToolbarButton';
// eslint-disable-next-line import/no-cycle
import isBlockActive from './isBlockActive';

export const LIST_TYPES = [ElementTypes.bulletedList, ElementTypes.numberedList];

export type BlockButtonProps = {
  format: ElementTypes,
  icon: KatIcon.Name
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const toggleBlock = (
  editor: Editor,
  format: ElementTypes,
  isActive: boolean,
) => {
  const hasVoid = Array.from(
    Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n)
        && Element.isElement(n)
        && Editor.isVoid(editor, n),
    }),
  ).length > 0;
  if (hasVoid) return;

  const otherFormat = format === ElementTypes.bulletedList
    ? ElementTypes.numberedList : ElementTypes.bulletedList;
  const isOtherActive = isBlockActive(editor, otherFormat);

  // Create list block
  if (!isActive && !isOtherActive) {
    const block = { type: format, children: [] } as BasicElement;
    Transforms.wrapNodes(editor, block);
    Transforms.setNodes<Element>(editor, { type: ElementTypes.listItem });
    // Toggle from current list format to selected list format
  } else if (!isActive && isOtherActive) {
    Transforms.setNodes<Element>(editor, { type: format }, {
      match: (n) => !Editor.isEditor(n)
        && Element.isElement(n)
        && LIST_TYPES.some((listType) => listType === n.type),
    });
    // Remove list block
  } else if (isActive && !isOtherActive) {
    Transforms.unwrapNodes(editor, {
      match: (n) => !Editor.isEditor(n)
          && Element.isElement(n)
          && LIST_TYPES.some((listType) => listType === n.type),
      split: true,
    });

    Transforms.setNodes<Element>(editor, { type: ElementTypes.paragraph }, {
      match: (n) => !Editor.isEditor(n)
          && Element.isElement(n)
          && n.type === 'list-item',
    });
  }
};

// Handles interactions with Block or Elements such as lists
const BlockButton = ({ format, icon, ...rest }: BlockButtonProps) => {
  const editor = useSlate();
  let isActive = false;
  if (editor.selection && ReactEditor.hasRange(editor, editor.selection)) {
    isActive = isBlockActive(editor, format);
  }

  return (
    <ToolbarButton
      type="button"
      isActive={isActive}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format, isActive);
      }}
      {...rest}
    >
      <KatIcon name={icon} size="small" />
    </ToolbarButton>
  );
};

export default BlockButton;
